import React, { createContext, useContext, useMemo } from 'react';

import { type TTemplate } from '@wp-block-builder/package';

import { useThrottledValue } from '@waypoint/react/hooks';

import getCustomColors from './helpers/getCustomColors';

type TState = {
  configuration: TTemplate;
  layoutConfiguration: TTemplate | null;
};

const Context = createContext<TState | null>(null);
type TProps = {
  children: JSX.Element;
  configuration: TTemplate;
  layoutConfiguration: TTemplate | null;
};

export function Provider({ children, configuration, layoutConfiguration }: TProps) {
  const value = useMemo<TState>(
    () => ({
      configuration,
      layoutConfiguration,
    }),
    [configuration, layoutConfiguration]
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useDocumentStoreContext() {
  return useContext(Context)!;
}

export function useStrictVariablesRendering() {
  return useContext(Context)!.configuration.strictVariablesRendering;
}

export function useDocumentColors(): string[] {
  const { configuration, layoutConfiguration } = useDocumentStoreContext();

  const internalConfiguration = useThrottledValue(configuration, 1000);
  const internalLayoutConfiguration = useThrottledValue(layoutConfiguration, 1000);
  return useMemo(() => {
    const c = getCustomColors(internalConfiguration);
    const l = getCustomColors(internalLayoutConfiguration);
    return [...new Set([...c, ...l])].sort();
  }, [internalConfiguration, internalLayoutConfiguration]);
}
